import React, { useRef } from "react";
import { Box, Typography, TextField, useTheme } from "@mui/material";
import { tokens } from "../../../../../theme";
import { EmailTemplateStepProps } from "../../types/types";
import InsertVariableMenu from "../InsertVaraibleMenu";

const fieldSx = {
  "& .MuiOutlinedInput-root": { borderRadius: "10px" },
};

const ContentStep: React.FC<EmailTemplateStepProps> = ({
  templateData,
  setTemplateData,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const subjectRef = useRef<HTMLInputElement | null>(null);
  const headingRef = useRef<HTMLInputElement | null>(null);
  const messageRef = useRef<HTMLTextAreaElement | null>(null);

  const handleChange =
    (field: keyof typeof templateData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setTemplateData((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const insertAt = (
    field: keyof typeof templateData,
    ref: React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>,
    variableKey: string,
  ) => {
    const el = ref.current;
    const currentValue = templateData[field] as string;

    if (!el) {
      setTemplateData((prev) => ({
        ...prev,
        [field]: currentValue + variableKey,
      }));
      return;
    }

    const start = el.selectionStart ?? currentValue.length;
    const end = el.selectionEnd ?? currentValue.length;
    const nextValue =
      currentValue.slice(0, start) + variableKey + currentValue.slice(end);

    setTemplateData((prev) => ({ ...prev, [field]: nextValue }));

    requestAnimationFrame(() => {
      el.focus();
      const caret = start + variableKey.length;
      el.setSelectionRange(caret, caret);
    });
  };

  const FieldLabelRow = ({
    label,
    required,
    onInsert,
  }: {
    label: string;
    required?: boolean;
    onInsert: (key: string) => void;
  }) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 0.75,
      }}
    >
      <Typography
        sx={{
          fontSize: "0.85rem",
          fontWeight: 500,
          color: colors.primary[700],
        }}
      >
        {label}
        {required && (
          <Box component="span" sx={{ color: "#E53E3E" }}>
            *
          </Box>
        )}
      </Typography>
      <InsertVariableMenu onInsert={onInsert} />
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Typography
        variant="h6"
        sx={{ fontWeight: 700, color: colors.primary[700] }}
      >
        Content
      </Typography>

      <Box>
        <FieldLabelRow
          label="Subject"
          required
          onInsert={(key) => insertAt("subject", subjectRef, key)}
        />
        <TextField
          fullWidth
          size="small"
          inputRef={subjectRef}
          placeholder="e.g Your account has been verified,{{user_name}}"
          value={templateData.subject}
          onChange={handleChange("subject")}
          sx={fieldSx}
        />
      </Box>

      <Box>
        <FieldLabelRow
          label="Heading"
          onInsert={(key) => insertAt("heading", headingRef, key)}
        />
        <TextField
          fullWidth
          size="small"
          inputRef={headingRef}
          value={templateData.heading}
          onChange={handleChange("heading")}
          sx={fieldSx}
        />
      </Box>

      <Box>
        <FieldLabelRow
          label="Email Message"
          required
          onInsert={(key) => insertAt("email_message", messageRef, key)}
        />
        <TextField
          fullWidth
          multiline
          minRows={5}
          inputRef={messageRef}
          placeholder="Dear {{user_name}}"
          value={templateData.email_message}
          onChange={handleChange("email_message")}
          sx={fieldSx}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              fontSize: "0.85rem",
              fontWeight: 500,
              color: colors.primary[700],
              mb: 0.75,
            }}
          >
            Button Label
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={templateData.button_label}
            onChange={handleChange("button_label")}
            sx={fieldSx}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              fontSize: "0.85rem",
              fontWeight: 500,
              color: colors.primary[700],
              mb: 0.75,
            }}
          >
            Button Url
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={templateData.button_url}
            onChange={handleChange("button_url")}
            sx={fieldSx}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ContentStep;
